import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthOptions } from '../../../../../hooks/useAuthOptions';
import { useCarValuationToolContext } from './CarValuationTool.context';

import {
  useUserVehicleQuery,
  useUserVehicleQueryKeys,
} from './queries/hooks/UserVehicle.queries';

import { ON_SIGN_IN_VALUE } from './CarValuationTool.constants';

import { fireToast } from '../../../../../helpers/Toasts';
import { rg4js } from '../../../../../helpers/raygun';
import { PAGE } from '../../../../../helpers/pages';
import { scrollElementIntoView } from '../../../../../helpers/scroll';

import { AUTHENTICATION_STATUSES } from '../../types';

/**
 * We use this hook to determine the content of the car valuation tool on page load,
 * based on the authentication status of the user, the presence of session storage,
 * and whether the user has a vehicle associated with their account.
 *
 * The loading state is determined by the status of the request and the user's authentication status.
 * We should only display the loading state if the request for data is incomplete
 * or whether it is not yet known if the user is authenticated or unauthenticated.
 *
 * @returns the data or error associated with the request for the vehicle
 * associated with the user's account, and a value to determine whether or not to show a loading state.
 */
const useCarValuationTool = () => {
  const { status } = useAuthOptions();

  const { useGetUserVehicleQuery } = useUserVehicleQuery();

  const queryClient = useQueryClient();

  const {
    data: carValuationData,
    isPending,
    isError,
    isSuccess,
    refetch,
    isRefetching,
  } = useGetUserVehicleQuery();

  const {
    setRegistrationNumber,
    setIsValueCarModalOpen,
    removeCarValuationToolSessionStorageData,
    sessionStorageSigninKey,
    sessionStorageRegistrationNumber,
  } = useCarValuationToolContext();

  const carValuationToolRef = useRef<HTMLDivElement | null>(null);

  /*
   * If an unauthenticated user has previously interacts with the tool, we direct them to the sign-in page.
   *
   * Once they are returned to the original page, the tool should scroll into view, based on the
   * presence of session storage set when they initially interacted with the tool, before being redirected.
   */
  const scrollCarValuationToolIntoView = useCallback(() => {
    if (carValuationToolRef?.current) {
      scrollElementIntoView({
        element: carValuationToolRef.current,
      });
    }
  }, []);

  const signedOutWithUserVehicleDisplayed =
    status === AUTHENTICATION_STATUSES.UNAUTHENTICATED && carValuationData;

  useEffect(() => {
    if (status === AUTHENTICATION_STATUSES.AUTHENTICATED && !isPending) {
      if (carValuationData) {
        /*
         * If the user decides to update their vehicle,
         * the registration number they previously entered while unauthenticated
         * is passed to the registration number input in the modal.
         *
         * We do not open the modal, but the option to do so is available from the
         * component used to display the details of the user's vehicle.
         */
        if (sessionStorageRegistrationNumber) {
          setRegistrationNumber(sessionStorageRegistrationNumber);
          removeCarValuationToolSessionStorageData();
          scrollCarValuationToolIntoView();
        }
      } else if (
        sessionStorageSigninKey === ON_SIGN_IN_VALUE &&
        sessionStorageRegistrationNumber
      ) {
        /**
         * Given that the user is authenticated and the session storage data has been set,
         * we open the modal populated with the registration number the user originally entered,
         * and scroll the panel into view in the background
         */
        scrollCarValuationToolIntoView();
        if (isSuccess) {
          setIsValueCarModalOpen(true);
        }
        setRegistrationNumber(sessionStorageRegistrationNumber);
        removeCarValuationToolSessionStorageData();
      }
      return;
    }
    if (
      sessionStorageSigninKey === ON_SIGN_IN_VALUE &&
      status === AUTHENTICATION_STATUSES.UNAUTHENTICATED
    ) {
      scrollCarValuationToolIntoView();
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      rg4js('send', {
        error: new Error(
          'User failed to sign in after being redirected from car valuation tool',
        ),
        tags: [PAGE.AD_DETAILS],
      });
      removeCarValuationToolSessionStorageData();
    }
    if (signedOutWithUserVehicleDisplayed) {
      queryClient.removeQueries({ queryKey: useUserVehicleQueryKeys.get });
    }
  }, [
    status,
    setRegistrationNumber,
    setIsValueCarModalOpen,
    carValuationData,
    isPending,
    sessionStorageSigninKey,
    sessionStorageRegistrationNumber,
    removeCarValuationToolSessionStorageData,
    scrollCarValuationToolIntoView,
    isSuccess,
    queryClient,
    signedOutWithUserVehicleDisplayed,
  ]);

  /*
   * A request for car valuation data will not be made unless the user is authenticated, otherwise isPending will remain true.
   * We therefore check if a request is in progress based on whether the user is authenticated and vehicle data is being fetched (isPending)
   * or refetched (isRefetching) if the user updates their vehicle through the modal, or if the authentication status is still loading.
   */
  const isLoading =
    (status === AUTHENTICATION_STATUSES.AUTHENTICATED &&
      (isPending || isRefetching)) ||
    status === AUTHENTICATION_STATUSES.LOADING;

  const errorOnClickHandler = () => {
    if (isError) {
      refetch();
    }
  };

  return {
    carValuationData,
    isLoading,
    isError,
    errorOnClickHandler,
    carValuationToolRef,
  };
};

export { useCarValuationTool };
